import type { AxiosError } from "axios";

/**
 * Handled client outcomes: the UI already reacts to these, so a Sentry event
 * for them is noise. Everything else (5xx, unexpected codes) is a defect.
 */
const HANDLED_STATUSES = new Set([400, 401, 402, 403, 404, 409, 422]);

const UUID_SEGMENT =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const NUMERIC_SEGMENT = /^\d+$/;

/**
 * Collapses a request URL to its route shape: scheme, host, query and hash
 * are dropped, UUID and numeric path segments become `:id`.
 */
export function routeTemplate(url: string | undefined): string {
  if (!url) return "unknown";
  const path = url
    .replace(/^[a-z][a-z0-9+.-]*:\/\/[^/]*/i, "")
    .split(/[?#]/)[0];
  return path
    .split("/")
    .map((segment) =>
      UUID_SEGMENT.test(segment) || NUMERIC_SEGMENT.test(segment)
        ? ":id"
        : segment,
    )
    .join("/");
}

export type ApiErrorCaptureContext = {
  fingerprint: string[];
  tags: { api_route: string; api_status: string };
};

/**
 * Decides whether a failed call reaches Sentry and how it groups. Returns
 * null for handled 4xx and for a call that never got an answer (network
 * error, abort, timeout — status undefined or 0); otherwise a fingerprint of
 * method + route template + status so each failing route is its own issue.
 *
 * Takes the parts rather than an `AxiosError` so an answer that arrives
 * inside another call's body — one item of the grouped batch get —
 * is judged by the same policy as the single request it stands in for.
 */
export function apiCaptureContext(
  method: string,
  url: string | undefined,
  status: number | undefined,
): ApiErrorCaptureContext | null {
  if (status === undefined || status === 0 || HANDLED_STATUSES.has(status)) {
    return null;
  }

  const route = routeTemplate(url);
  const apiStatus = String(status);
  return {
    fingerprint: [method.toUpperCase(), route, apiStatus],
    tags: { api_route: route, api_status: apiStatus },
  };
}

export function apiErrorCaptureContext(
  error: AxiosError,
): ApiErrorCaptureContext | null {
  return apiCaptureContext(
    error.config?.method ?? "unknown",
    error.config?.url,
    error.response?.status,
  );
}
